import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  /** @type {boolean} */
  #isAnimating = false;

  /** @type {{ cancel: () => void, direction: 'open' | 'close' } | null} */
  #animation = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState(false);
    this.classList.add('accordion-custom--initialized');

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    this.#cancelAnimation();
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.#toggle();
  };

  /**
   * Cancels any pending accordion animation and clears its listeners/timeouts.
   */
  #cancelAnimation() {
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    if (this.#animation) {
      this.#animation.cancel();
      this.#animation = null;
    }
    this.#isAnimating = false;
    const content = this.#getContent();
    if (content) {
      content.classList.remove('details-content--closing');
    }
    this.details.classList.remove('details-is-closing');
  }

  /**
   * Toggles the accordion with a smooth height/opacity animation.
   */
  #toggle() {
    if (this.#isAnimating) {
      if (this.#animation?.direction === 'open') {
        this.#cancelAnimation();
        this.#closeWithAnimation();
      } else if (this.#animation?.direction === 'close') {
        this.#cancelAnimation();
        this.#openWithAnimation();
      }
      return;
    }

    if (this.details.open) {
      this.#closeWithAnimation();
    } else {
      this.#openWithAnimation();
    }
  }

  /**
   * Checks if the user prefers reduced motion.
   * @returns {boolean}
   */
  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Returns the animated content wrapper inside the details element.
   * @returns {HTMLElement | null}
   */
  #getContent() {
    const content = this.details.querySelector('.details-content');
    return content instanceof HTMLElement ? content : null;
  }

  /**
   * Syncs the animated content styles with the current details.open state.
   */
  #syncOpenStyles() {
    const content = this.#getContent();
    if (!content) return;
    if (this.details.open) {
      const height = content.scrollHeight;
      if (height > 0) {
        content.style.setProperty('--accordion-content-height', `${height}px`);
      } else {
        content.style.maxHeight = 'none';
      }
      content.classList.add('details-content--open');
    } else {
      content.classList.remove('details-content--open', 'details-content--closing');
      content.style.maxHeight = '';
      content.style.removeProperty('--accordion-content-height');
      this.details.classList.remove('details-is-closing');
    }
  }

  /**
   * Animates the accordion open.
   */
  #openWithAnimation() {
    if (this.#prefersReducedMotion()) {
      this.details.open = true;
      this.#syncOpenStyles();
      return;
    }

    if (this.#isAnimating) {
      if (this.#animation?.direction === 'open') return;
      this.#cancelAnimation();
    }

    const content = this.#getContent();
    if (!content) {
      this.details.open = true;
      return;
    }

    this.#isAnimating = true;
    this.details.open = true;

    this.#animation = {
      direction: 'open',
      cancel: () => {
        if (this.#rafId) {
          cancelAnimationFrame(this.#rafId);
          this.#rafId = null;
        }
      },
    };

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;

      const height = content.scrollHeight;
      if (height > 0) {
        content.style.setProperty('--accordion-content-height', `${height}px`);
      } else {
        content.style.maxHeight = 'none';
      }
      content.classList.add('details-content--open');

      /** @type {number | undefined} */
      let timeoutId;
      /**
       * @param {Event} event
       */
      const cleanup = (event) => {
        if (event instanceof TransitionEvent) {
          if (event.target !== content) return;
          if (event.propertyName !== 'max-height' && event.propertyName !== 'opacity') return;
        }
        clearTimeout(timeoutId);
        if (!this.#isAnimating) return;
        if (!content.closest('.facets--horizontal')) {
          content.style.maxHeight = 'none';
          content.style.removeProperty('--accordion-content-height');
        }
        this.#isAnimating = false;
        this.#animation = null;
      };

      const cancel = () => {
        clearTimeout(timeoutId);
        content.removeEventListener('transitionend', cleanup);
      };

      timeoutId = setTimeout(cleanup, 500);
      content.addEventListener('transitionend', cleanup, { once: true });
      this.#animation = { cancel, direction: 'open' };
    });
  }

  /**
   * Animates the accordion closed.
   */
  #closeWithAnimation() {
    if (this.#prefersReducedMotion()) {
      this.details.open = false;
      this.#syncOpenStyles();
      return;
    }

    if (this.#isAnimating) {
      if (this.#animation?.direction === 'close') return;
      this.#cancelAnimation();
    }

    const content = this.#getContent();
    if (!content) {
      this.details.open = false;
      return;
    }

    this.#isAnimating = true;
    this.details.classList.add('details-is-closing');

    content.style.maxHeight = '';
    content.style.setProperty('--accordion-content-height', `${content.scrollHeight}px`);
    content.offsetHeight;
    content.classList.add('details-content--closing');
    content.classList.remove('details-content--open');

    /** @type {number | undefined} */
    let timeoutId;
    /**
     * @param {Event} event
     */
    const cleanup = (event) => {
      if (event instanceof TransitionEvent) {
        if (event.target !== content) return;
        if (event.propertyName !== 'max-height' && event.propertyName !== 'opacity') return;
      }
      clearTimeout(timeoutId);
      if (!this.#isAnimating) return;
      this.details.open = false;
      this.#syncOpenStyles();
      this.#isAnimating = false;
      this.#animation = null;
    };

    const cancel = () => {
      clearTimeout(timeoutId);
      content.removeEventListener('transitionend', cleanup);
    };

    timeoutId = setTimeout(cleanup, 500);
    content.addEventListener('transitionend', cleanup, { once: true });
    this.#animation = { cancel, direction: 'close' };
  }

  /**
   * Handles the toggle event.
   */
  #handleToggle = () => {
    if (!this.#isAnimating) {
      this.#syncOpenStyles();
    }

    if (!this.hasAttribute('data-exclusive') || !this.details.open) return;

    const sharedList = this.closest('ul.has-submenu');
    if (!sharedList) return;

    for (const sibling of sharedList.querySelectorAll('accordion-custom[data-exclusive]')) {
      if (sibling !== this && sibling instanceof AccordionCustom && sibling.details.open) {
        sibling.#closeWithAnimation();
      }
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState(false);
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   * @param {boolean} [animate=true] - Whether to animate the state change.
   */
  #setDefaultOpenState(animate = true) {
    this.#cancelAnimation();

    const content = this.#getContent();
    if (content) {
      content.classList.remove('details-content--open', 'details-content--closing');
      content.style.maxHeight = '';
      content.style.removeProperty('--accordion-content-height');
    }

    const isMobile = isMobileBreakpoint();
    const shouldBeOpen =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    if (shouldBeOpen === this.details.open) {
      if (shouldBeOpen) this.#syncOpenStyles();
      return;
    }

    if (animate) {
      this.#toggle();
    } else {
      this.details.open = shouldBeOpen;
      this.#syncOpenStyles();
    }
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape && this.details.open) {
      event.preventDefault();

      this.#closeWithAnimation();
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
